import { useState, type FC, useEffect, type ReactNode, useRef, ComponentProps } from 'react';
import { Button, InputText, Select, Space } from '@g2u/ui-kit';
import { SearchOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { Form } from 'antd';

import { addSpacesToNumber, useSearchParamsInUrl } from 'helpers';
import { useTechnicianSearch } from 'helpers/hooks/useTechnicianSearch';
import { TechnicianList } from '../technician-list';
import { TechnicianSearchParams } from 'queries';
import { Condition, type WithOptionalDataTest } from 'types';
import styles from './search-technician.module.scss';

type Props = {
  actions?: ComponentProps<typeof TechnicianList>['actions'];
  emptyContent?: (data: FormFields) => ReactNode;
  syncParamsToUrl?: boolean;
} & WithOptionalDataTest;

type LocalSearchParams = {
  searchBy: TechnicianSearchParams['searchParam'];
  searchValue: string;
  page: number;
  recordsPerPage: number;
};

type SearchByOption<K extends keyof TechnicianSearchParams> = {
  label: ReactNode;
  value: NonNullable<TechnicianSearchParams[K]>;
};

const searchByDetailsOptions: SearchByOption<'searchParam'>[] = [
  { label: <>Name</>, value: 'byName' },
  { label: <>ID</>, value: 'byID' },
  { label: <>Email</>, value: 'byEmail' },
  { label: <>Address</>, value: 'byAddress' },
  { label: <>State</>, value: 'byState' },
  { label: <>Mobile</>, value: 'byMobile' },
  { label: <>Phone</>, value: 'byPhone' },
];

const searchByStatusOptions: SearchByOption<'status'>[] = [
  { label: <>All</>, value: '' },
  { label: <>Active</>, value: Condition.Yes },
  { label: <>Inactive</>, value: Condition.No },
];

type FormFields = {
  searchBy: TechnicianSearchParams['searchParam'];
  searchedValue: string;
  status: Condition;
};

export const SearchTechnician: FC<Props> = ({ actions, emptyContent, syncParamsToUrl = false }) => {
  const [form] = Form.useForm<FormFields>();
  const { state } = useLocation();

  const initialSearchParams: LocalSearchParams = {
    searchBy: 'byName',
    searchValue: '',
    page: 1,
    recordsPerPage: 5,
  };

  const [localParams, setLocalParams] = useState<LocalSearchParams>(initialSearchParams);
  const [urlParams, setUrlParams] = useSearchParamsInUrl<LocalSearchParams>(
    syncParamsToUrl ? initialSearchParams : undefined,
  );

  const params: LocalSearchParams = syncParamsToUrl ? urlParams : localParams;

  const setParams = (p: Partial<LocalSearchParams>) => {
    if (syncParamsToUrl) setUrlParams(p);
    else setLocalParams((prev) => ({ ...prev, ...p }));
  };

  const formSearchBy = Form.useWatch('searchBy', { form });
  const searchByNumber = formSearchBy && /(byPhone|byMobile|byID)/.test(formSearchBy);
  const prevSearchByNumber = useRef<boolean>(false);

  useEffect(() => {
    if (searchByNumber && prevSearchByNumber.current === false) {
      form.setFieldValue('searchedValue', '');
    }
    prevSearchByNumber.current = !!searchByNumber;
  }, [searchByNumber]);

  useEffect(() => {
    if (state?.searchValue) {
      form.setFieldValue('searchedValue', state.searchValue);
      setParams({ searchBy: 'byName', searchValue: state.searchValue, page: 1 });
    }
  }, [state]);

  const handleFormSubmit = (fields: FormFields) => {
    setParams({
      searchBy: fields.searchBy,
      searchValue: fields.searchedValue,
      page: 1,
    });
  };

  const {
    technicians,
    total = 0,
    isLoading,
  } = useTechnicianSearch({
    searchParam: params.searchBy,
    searchValue: params.searchValue,
    page: params.page,
    perPage: params.recordsPerPage,
    status: form.getFieldValue('status'),
  });

  const paginationMetadata: ComponentProps<typeof TechnicianList>['paginationMetadata'] = {
    total,
    current_page: params.page,
    pageSize: params.recordsPerPage,
    onChange(page, recordsPerPage) {
      setParams({ page, recordsPerPage });
    },
  };

  return (
    <div className={styles.searchTechnicianFile}>
      <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          <Space.$ compact fullWidth>
            <Select.$ name="status" initialValue="" options={searchByStatusOptions} label="Status: " />
            <Select.$
              name="searchBy"
              initialValue="byName"
              options={searchByDetailsOptions}
              label="Account Details: "
            />
            {searchByNumber ? (
              <InputText.$
                fullWidth
                name="searchedValue"
                placeholder=""
                maxLength={12}
                label="  "
                formatter={(val) => addSpacesToNumber(val?.toString(), [4, 7])}
              />
            ) : (
              <InputText.$ fullWidth name="searchedValue" placeholder="" label=" " />
            )}
            <Form.Item label=" ">
              <Button.$
                dataTestId="search-techs-btn"
                type={Button.Type.Primary}
                icon={<SearchOutlined />}
                htmlType="submit"
              />
            </Form.Item>
          </Space.$>
        </Form>
      </Space.$>
      <TechnicianList
        isLoading={isLoading}
        technicians={technicians}
        actions={actions}
        paginationMetadata={paginationMetadata}
      />
      {technicians && !technicians.length ? emptyContent?.(form.getFieldsValue()) : null}
    </div>
  );
};
